/**
 * ShareCardRender — 1080x1920 vertical card, rendered as real DOM so KaTeX
 * math and rich text render correctly. Mount once (off-screen), capture via
 * `html-to-image` when the user taps Share.
 *
 * The card previously used Canvas 2D which can't draw math. Now the student
 * sees exactly what the past-exam UI shows: MarkdownRenderer for question +
 * options, identical remark-math / rehype-katex pipeline.
 */

import React, { forwardRef, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import MarkdownRenderer from './MicroLearning/MarkdownRenderer';

export interface ShareCardRenderProps {
  questionText: string;
  options: { label: string; text: string }[];
  correctAnswer?: string;
  subject?: string;
  year?: number | string;
  topic?: string;
  url: string;
}

const W = 1080;
const H = 1920;

const BG = '#0a0a0a';
const ACCENT = '#84cc16';
const TEXT = '#f5f5f5';
const TEXT_DIM = 'rgba(255, 255, 255, 0.6)';
const OPTION_BG = 'rgba(255, 255, 255, 0.05)';
const OPTION_BORDER = 'rgba(255, 255, 255, 0.12)';
const CORRECT_BG = 'rgba(132, 204, 22, 0.18)';
const CORRECT_BORDER = 'rgba(132, 204, 22, 0.75)';

const ShareCardRender = forwardRef<HTMLDivElement, ShareCardRenderProps>(
  ({ questionText, options, correctAnswer, subject, year, topic, url }, ref) => {
    const [qrDataUrl, setQrDataUrl] = useState<string>('');

    useEffect(() => {
      let cancelled = false;
      QRCode.toDataURL(url, {
        width: 280,
        margin: 0,
        color: { dark: '#0a0a0a', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      })
        .then(d => {
          if (!cancelled) setQrDataUrl(d);
        })
        .catch(() => {
          if (!cancelled) setQrDataUrl('');
        });
      return () => {
        cancelled = true;
      };
    }, [url]);

    const ribbonParts: string[] = [];
    if (subject) ribbonParts.push(subject);
    if (year) ribbonParts.push(`${year} ESSLCE`);
    if (topic) ribbonParts.push(topic);
    const ribbonText = ribbonParts.join(' · ');

    const correct = (correctAnswer || '').toUpperCase();
    const displayUrl = url.replace(/^https?:\/\//, '');

    // Adaptive sizing — more options or longer text = smaller base
    const optCount = options.length || 4;
    const optionFontSize = optCount > 4 ? 30 : 34;

    return (
      <div
        ref={ref}
        style={{
          width: `${W}px`,
          height: `${H}px`,
          background: BG,
          color: TEXT,
          fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
          display: 'flex',
          flexDirection: 'column',
          padding: '80px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          backgroundImage:
            'radial-gradient(ellipse at 50% -10%, rgba(132,204,22,0.22), transparent 60%),' +
            'linear-gradient(to bottom, transparent 70%, rgba(0,0,0,0.4) 100%)',
        }}
      >
        {/* ── Header ─────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              fontSize: '64px',
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            <img
              src="/logo192.png"
              alt=""
              width={84}
              height={84}
              style={{ display: 'block', flexShrink: 0, objectFit: 'contain' }}
            />
            <span>Lomi</span>
            <span style={{ color: TEXT_DIM, fontWeight: 700 }}>ሎሚ</span>
          </div>
          <div style={{ fontSize: '34px', color: TEXT_DIM, fontWeight: 500 }}>
            ESSLCE practice
          </div>
        </div>

        {/* ── Subject ribbon ─────────────────────────────── */}
        {ribbonText && (
          <div
            style={{
              alignSelf: 'flex-start',
              padding: '14px 28px',
              background: 'rgba(132,204,22,0.14)',
              border: '2px solid rgba(132,204,22,0.5)',
              color: ACCENT,
              borderRadius: '30px',
              fontSize: '30px',
              fontWeight: 600,
              marginBottom: '48px',
            }}
          >
            {ribbonText}
          </div>
        )}

        {/* ── Question ──────────────────────────────────── */}
        <div
          style={{
            fontSize: '44px',
            fontWeight: 700,
            lineHeight: 1.35,
            marginBottom: '40px',
            color: TEXT,
            flex: '0 0 auto',
            maxHeight: '480px',
            overflow: 'hidden',
          }}
          className="share-card-question"
        >
          <MarkdownRenderer>{questionText || ''}</MarkdownRenderer>
        </div>

        {/* ── Options ────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: 'auto',
          }}
          className="share-card-options"
        >
          {options.map(opt => {
            const label = (opt.label || '').toUpperCase();
            const isCorrect = !!correct && label === correct;
            return (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  padding: '20px 26px',
                  background: isCorrect ? CORRECT_BG : OPTION_BG,
                  border: `2px solid ${isCorrect ? CORRECT_BORDER : OPTION_BORDER}`,
                  borderRadius: '16px',
                  fontSize: `${optionFontSize}px`,
                  lineHeight: 1.3,
                  color: TEXT,
                }}
              >
                <span
                  style={{
                    flex: '0 0 56px',
                    height: '56px',
                    width: '56px',
                    borderRadius: '50%',
                    background: isCorrect ? ACCENT : 'rgba(255,255,255,0.08)',
                    color: isCorrect ? '#0a0a0a' : TEXT,
                    fontWeight: 700,
                    fontSize: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {label}
                </span>
                <span style={{ flex: 1 }}>
                  <MarkdownRenderer inline>{opt.text || ''}</MarkdownRenderer>
                </span>
                {isCorrect && (
                  <span style={{ color: ACCENT, fontWeight: 700, fontSize: '28px' }}>
                    ✓
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Bottom band ────────────────────────────────── */}
        <div
          style={{
            marginTop: '40px',
            paddingTop: '32px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '32px',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '48px', fontWeight: 700, marginBottom: '18px' }}>
              Try it on Lomi
            </div>
            <div style={{ fontSize: '30px', color: TEXT_DIM, marginBottom: '22px' }}>
              Free past-exam practice with AI explanations.
            </div>
            <div
              style={{
                fontSize: '32px',
                color: ACCENT,
                fontFamily: 'Menlo, "Courier New", monospace',
                fontWeight: 600,
                wordBreak: 'break-all',
              }}
            >
              {displayUrl}
            </div>
          </div>
          {qrDataUrl && (
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  background: '#fff',
                  padding: '24px',
                  borderRadius: '20px',
                  display: 'inline-block',
                }}
              >
                <img
                  src={qrDataUrl}
                  alt=""
                  width={240}
                  height={240}
                  style={{ display: 'block' }}
                />
              </div>
              <div style={{ color: TEXT_DIM, fontSize: '26px', marginTop: '16px' }}>
                Scan to try
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ────────────────────────────────────── */}
        <div
          style={{
            textAlign: 'center',
            color: 'rgba(255,255,255,0.35)',
            fontSize: '26px',
            marginTop: '24px',
            fontWeight: 500,
          }}
        >
          lomistudy.com · @lomistudybot
        </div>
      </div>
    );
  },
);

ShareCardRender.displayName = 'ShareCardRender';

export default ShareCardRender;
